/**
 * DesignerInvoiceReviewPanel — staff HITL review of pending designer invoices.
 * Side-by-side PDF (left) + editable allocation lines mapped to deals/projects (right).
 * Approve blocked until gates pass. Badges only after approve (not shown on pending).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X, Loader2, FileText, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import QBDealPicker, { type QBDealOption } from '../sales-activity/QBDealPicker';
import { getProjectDisplayName } from '../../utils/projectName';
import { phaseLabel } from '../../utils/qbDealLinkOptions';
import type { TimberProject } from '../../types';
import {
  subscribePendingDesignerInvoices,
  resolveDesignerInvoicePdfUrl,
  saveDesignerInvoiceReview,
  saveDesignerInvoicePaymentMethod,
  approveDesignerInvoice,
  rejectDesignerInvoice,
  evaluateClientApproveGates,
  formatInvoiceMoney,
  newEmptyLine,
  withResolvedHubProjects,
  resolveDesignerInvoiceHubProject,
  DESIGNER_INVOICES_INDEX_HINT,
  type DesignerInvoiceDoc,
  type DesignerInvoiceLine,
  type DesignerInvoicePhase,
  type DesignerInvoiceBucket,
} from '../../utils/designerInvoiceService';
import { invoiceNeedsJobPurchaseOrders } from '../../utils/designerInvoicePurchase';
import { attemptDesignerInvoiceQboBills } from '../../utils/designerInvoicePurchaseSubmit';
import {
  DESIGNER_INVOICE_PAYMENT_METHOD_DEFAULT_NOTE,
  DESIGNER_INVOICE_PAYMENT_METHOD_OPTIONS,
  designerInvoicePaymentMethodLabel,
  primaryDesignerInvoicePhase,
  resolveDesignerInvoicePaymentMethodId,
  type DesignerInvoicePaymentMethodId,
} from '../../utils/designerInvoicePayment';
import { canReviewDesignerInvoices } from '../../utils/projectPLAccess';
import './DesignerInvoiceReviewPanel.css';

const PHASES: { value: DesignerInvoicePhase; label: string }[] = [
  { value: 'estimating', label: 'Estimating' },
  { value: 'precon', label: 'Precon' },
  { value: 'under_contract', label: 'Under contract' },
  { value: 'overhead', label: 'Overhead (OpEx)' },
];

const BUCKETS: { value: DesignerInvoiceBucket; label: string; hint: string }[] = [
  {
    value: 'external_design',
    label: 'External design',
    hint: '1099 designer work on this job — drawings, revisions, renderings, site visits.',
  },
  {
    value: 'external_engineering',
    label: 'External engineering',
    hint: 'PE / engineering firm work on this job — calcs, stamps, letters (e.g. FTET).',
  },
  {
    value: 'training_onboarding',
    label: 'Training / onboarding',
    hint: 'Designer time spent training LCTF staff or onboarding, not producing job drawings.',
  },
  {
    value: 'other',
    label: 'Other',
    hint: 'Reimbursable, software, travel, or anything that is not job design, engineering, or training. Job-tagged other lines still issue an engineering PO on approve (compat).',
  },
];

function hubProjectOptionLabel(project: TimberProject): string {
  const name = project.name || project.id || 'Hub project';
  const phase = phaseLabel(project.phase);
  return phase ? `${name} · ${phase}` : name;
}

interface DesignerInvoiceReviewPanelProps {
  /** Hide the page header when mounted inside the Project P&L popup. */
  compact?: boolean;
  /** Called after the last pending invoice is approved or rejected. */
  onQueueEmpty?: () => void;
}

const DesignerInvoiceReviewPanel: React.FC<DesignerInvoiceReviewPanelProps> = ({
  compact = false,
  onQueueEmpty,
}) => {
  const { user } = useAuth();
  const { enhancedDeals, timberProjects, crmUsers } = useCRM();
  const me = crmUsers.find((u) => u.uid === user?.id);
  const canReview = canReviewDesignerInvoices({
    authRole: user?.role,
    designations: me?.designations ?? user?.designations,
    briefingProfile: user?.executiveBriefing?.profile ?? me?.executiveBriefing?.profile ?? null,
  });
  const prevCountRef = useRef<number | null>(null);

  const [invoices, setInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftLines, setDraftLines] = useState<DesignerInvoiceLine[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [usedFallback, setUsedFallback] = useState(false);
  const [issueNote, setIssueNote] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [draftInvoiceId, setDraftInvoiceId] = useState<string | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<DesignerInvoicePaymentMethodId>('project_card');
  const lastPersistedKeyRef = useRef('');
  const pendingSaveRef = useRef<{ invoiceId: string; lines: DesignerInvoiceLine[] } | null>(null);
  const saveTimerRef = useRef<number | null>(null);

  const dealOptions: QBDealOption[] = useMemo(() => {
    return enhancedDeals
      .filter((d): d is typeof d & { id: string } => Boolean(d.id))
      .map((d) => {
        const label = getProjectDisplayName(d) || d.title || d.id;
        const search = [d.projectNumber, d.clientLabel, d.descriptor, d.title, d.contactName, d.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return { id: d.id, label: String(label), searchText: search };
      });
  }, [enhancedDeals]);

  useEffect(() => {
    if (!canReview) {
      setLoading(false);
      setInvoices([]);
      return undefined;
    }
    return subscribePendingDesignerInvoices((list, meta) => {
      setLoading(false);
      setInvoices(list);
      setSubError(meta?.error || null);
      setUsedFallback(Boolean(meta?.usedFallback));
    });
  }, [canReview]);

  useEffect(() => {
    if (loading) return;
    const prev = prevCountRef.current;
    prevCountRef.current = invoices.length;
    if (prev != null && prev > 0 && invoices.length === 0) {
      onQueueEmpty?.();
    }
  }, [invoices.length, loading, onQueueEmpty]);

  useEffect(() => {
    setSelectedId((prev) => {
      if (prev && invoices.some((i) => i.id === prev)) return prev;
      return invoices[0]?.id ?? null;
    });
  }, [invoices]);

  const selected = invoices.find((i) => i.id === selectedId) || null;
  const linesLocked = selected?.status === 'pending_approval';

  const pendingApprovalStep = useMemo(() => {
    const steps = selected?.approvalSteps ?? [];
    return steps.find((step) => step.status === 'pending') ?? null;
  }, [selected?.approvalSteps]);

  const approvalStepSummary = useMemo(() => {
    const steps = selected?.approvalSteps ?? [];
    if (!steps.length) return null;
    const done = steps.filter((step) => step.status === 'approved' || step.status === 'skipped').length;
    return { done, total: steps.length };
  }, [selected?.approvalSteps]);

  const persistKey = (invoiceId: string, lines: DesignerInvoiceLine[]) =>
    JSON.stringify({ invoiceId, lines });

  const flushPendingSave = () => {
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    const invoice = invoices.find((row) => row.id === pending.invoiceId);
    if (invoice?.status === 'pending_approval') return;
    const key = persistKey(pending.invoiceId, pending.lines);
    lastPersistedKeyRef.current = key;
    void saveDesignerInvoiceReview(pending.invoiceId, pending.lines).catch((e) => {
      setError((e as Error).message);
    });
  };

  useEffect(() => {
    if (!selected) {
      setDraftLines([]);
      setDraftInvoiceId(null);
      setPdfUrl(null);
      lastPersistedKeyRef.current = '';
      return;
    }
    const lines = withResolvedHubProjects(
      selected.proposedLines.map((line) => ({ ...line })),
      timberProjects,
    );
    setDraftLines(lines);
    setDraftInvoiceId(selected.id);
    setPaymentMethodId(
      resolveDesignerInvoicePaymentMethodId({
        paymentMethodId: selected.paymentMethodId,
        phase: primaryDesignerInvoicePhase(lines, selected.reviewPhase),
        amount: selected.invoiceTotal,
      }),
    );
    lastPersistedKeyRef.current = persistKey(selected.id, lines);
    setError(null);
    setSaveState('idle');
    let cancelled = false;
    setPdfLoading(true);
    void resolveDesignerInvoicePdfUrl(selected).then((url) => {
      if (!cancelled) {
        setPdfUrl(url);
        setPdfLoading(false);
      }
    });
    return () => {
      cancelled = true;
      flushPendingSave();
    };
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDraftLines((prev) => withResolvedHubProjects(prev, timberProjects));
  }, [timberProjects]);

  const resolvedDraftLines = useMemo(
    () => withResolvedHubProjects(draftLines, timberProjects),
    [draftLines, timberProjects],
  );

  const gates = useMemo(
    () =>
      evaluateClientApproveGates({
        invoiceTotal: selected?.invoiceTotal ?? 0,
        lines: resolvedDraftLines,
      }),
    [resolvedDraftLines, selected?.invoiceTotal],
  );

  const issuePo = invoiceNeedsJobPurchaseOrders(resolvedDraftLines);

  useEffect(() => {
    if (selected?.status !== 'pending_approval') return;
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingSaveRef.current = null;
    setSaveState('idle');
  }, [selected?.status]);

  useEffect(() => {
    if (!selected || !user) return;
    if (draftInvoiceId !== selected.id) return;
    if (selected.status === 'pending_approval') return;
    const key = persistKey(selected.id, resolvedDraftLines);
    if (key === lastPersistedKeyRef.current) return;
    pendingSaveRef.current = { invoiceId: selected.id, lines: resolvedDraftLines };
    if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
    setSaveState('saving');
    const invoiceId = selected.id;
    const lines = resolvedDraftLines;
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      pendingSaveRef.current = null;
      lastPersistedKeyRef.current = persistKey(invoiceId, lines);
      void saveDesignerInvoiceReview(invoiceId, lines)
        .then(() => setSaveState('saved'))
        .catch((e) => {
          setSaveState('idle');
          setError((e as Error).message);
        });
    }, 400);
    return () => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [resolvedDraftLines, selected?.id, selected?.status, draftInvoiceId, user]);

  const projectsForDeal = (dealId: string | null | undefined) => {
    const list = timberProjects.filter((p) => p.id);
    if (!dealId) return list;
    return list.filter((p) => p.dealId === dealId);
  };

  const updateLine = (id: string, patch: Partial<DesignerInvoiceLine>) => {
    setDraftLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        if (next.phase === 'overhead' || !next.dealId) return next;
        return { ...next, ...resolveDesignerInvoiceHubProject(next, timberProjects) };
      }),
    );
  };

  const handlePaymentMethodChange = async (next: DesignerInvoicePaymentMethodId) => {
    if (!selected || !canReview) return;
    setPaymentMethodId(next);
    try {
      await saveDesignerInvoicePaymentMethod(selected.id, next);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleApprove = async () => {
    if (!selected || !user || !gates.ok || !canReview) return;
    flushPendingSave();
    setBusy(true);
    setError(null);
    setIssueNote(null);
    try {
      if (!linesLocked) {
        await saveDesignerInvoiceReview(selected.id, resolvedDraftLines);
      }
      await saveDesignerInvoicePaymentMethod(selected.id, paymentMethodId);
      // PO is issued server-side only on the FINAL approval gate — never on intermediate clicks.
      const approved = await approveDesignerInvoice(selected.id, resolvedDraftLines, {
        paymentMethodId,
      });
      const poNumbers = Array.isArray(approved.poNumbers)
        ? (approved.poNumbers as unknown[]).filter((n): n is string => typeof n === 'string' && Boolean(n))
        : [];
      const purchaseRequisitionIds = Array.isArray(approved.purchaseRequisitionIds)
        ? (approved.purchaseRequisitionIds as unknown[]).filter(
            (n): n is string => typeof n === 'string' && Boolean(n),
          )
        : [];
      const qboMessage = typeof approved.qboBillMessage === 'string' ? approved.qboBillMessage : '';
      if (approved.final === false) {
        setIssueNote(
          typeof approved.notes === 'string'
            ? approved.notes
            : 'Approval recorded. Waiting on the next person in the approval chain. No PO yet.',
        );
      } else if (approved.qboBillHandled === true) {
        const poLabel = poNumbers.length ? poNumbers.join(', ') : '';
        setIssueNote([poLabel, qboMessage].filter(Boolean).join(' · ') || null);
      } else if (purchaseRequisitionIds.length) {
        const qbNotes = await attemptDesignerInvoiceQboBills({
          invoice: { ...selected, paymentMethodId },
          issued: purchaseRequisitionIds.map((id, index) => ({
            purchaseRequisitionId: id,
            poNumber: poNumbers[index],
            dealId: '',
            projectId: '',
            amount: 0,
            reused: false,
          })),
          lines: resolvedDraftLines,
        });
        const poLabel = poNumbers.length ? poNumbers.join(', ') : issuePo ? 'PO issued' : '';
        const payLabel = `Pay by ${designerInvoicePaymentMethodLabel(paymentMethodId)}`;
        setIssueNote([poLabel, payLabel, ...qbNotes].filter(Boolean).join(' · '));
      } else if (typeof approved.notes === 'string') {
        setIssueNote(approved.notes);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !user || !canReview) return;
    pendingSaveRef.current = null;
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const reason = window.prompt('Reject reason (optional)') || undefined;
    setBusy(true);
    setError(null);
    try {
      await rejectDesignerInvoice(selected.id, reason);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`dir-root${compact ? ' dir-root--compact' : ''}`} data-testid="designer-invoice-reviews">
      {!compact && (
        <div className="dir-header">
          <h2 className="dir-title">
            <FileText size={16} aria-hidden />
            Designer invoice reviews
            {invoices.length > 0 && <span className="dir-count">{invoices.length}</span>}
          </h2>
          <p className="dir-hint">
            Map invoice cash to deals/projects (or overhead OpEx). Approve only when every non-OH line
            has deal + project and line amounts sum to the invoice total ± $0.01. Job-direct lines
            issue a CRM PO on the <em>final</em> approval gate only (card or Bill Payment). Estimating
            / precon stay PO-only — no Billing &amp; Payables row. Badges appear after approve.
          </p>
        </div>
      )}

      {loading && (
        <div className="dir-status">
          <Loader2 size={16} className="dir-spin" /> Loading pending invoices…
        </div>
      )}

      {!loading && !canReview && (
        <div className="dir-status">
          Designer invoices are reviewed by finance and office ops, not operations.
        </div>
      )}

      {!loading && subError && (
        <div className="dir-status dir-status--error">
          {subError}
          {usedFallback && <p className="dir-status-hint">{DESIGNER_INVOICES_INDEX_HINT}</p>}
        </div>
      )}

      {!loading && !subError && invoices.length === 0 && (
        <div className={`dir-status${issueNote ? ' dir-status--ok' : ''}`}>
          {issueNote || 'No pending designer invoices.'}
        </div>
      )}

      {!loading && invoices.length > 0 && (
        <div className="dir-body">
          <aside className="dir-queue">
            <ul>
              {invoices.map((inv) => (
                <li key={inv.id}>
                  <button
                    type="button"
                    className={`dir-queue-item ${inv.id === selectedId ? 'active' : ''}`}
                    onClick={() => setSelectedId(inv.id)}
                  >
                    <span className="dir-queue-vendor">{inv.vendor || 'Vendor'}</span>
                    <span className="dir-queue-meta">
                      {/* Pending: show source only — not invoice # badge */}
                      {inv.source || 'agent'} · {formatInvoiceMoney(inv.invoiceTotal)}
                    </span>
                    <span className="dir-queue-file">{inv.fileName || inv.invoiceDate || inv.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {selected && (
            <div className="dir-main">
              <div className="dir-pdf">
                {pdfLoading && (
                  <div className="dir-status">
                    <Loader2 size={16} className="dir-spin" /> Loading PDF…
                  </div>
                )}
                {!pdfLoading && pdfUrl && (
                  <iframe title="Designer invoice PDF" src={pdfUrl} className="dir-pdf-frame" />
                )}
                {!pdfLoading && !pdfUrl && (
                  <div className="dir-status">
                    PDF unavailable. The file is stored, but a download link could not be minted.
                    Refresh, or reopen this invoice after the latest functions deploy.
                  </div>
                )}
              </div>

              <div className="dir-editor">
                <div className="dir-editor-head">
                  <div>
                    <div className="dir-vendor">{selected.vendor}</div>
                    <div className="dir-total">
                      Invoice total {formatInvoiceMoney(selected.invoiceTotal)}
                      {selected.invoiceDate ? ` · ${selected.invoiceDate}` : ''}
                    </div>
                    {/* Invoice # badge withheld until approve */}
                    <div className="dir-pending-note">
                      {linesLocked
                        ? 'In approval — mapping locked until reject'
                        : 'Pending review — invoice # badge after approve'}
                      {!linesLocked && saveState === 'saving' && (
                        <span className="dir-save-hint">Saving…</span>
                      )}
                      {!linesLocked && saveState === 'saved' && (
                        <span className="dir-save-hint">Saved</span>
                      )}
                    </div>
                    {linesLocked && approvalStepSummary && pendingApprovalStep ? (
                      <p className="dir-approval-gates">
                        {selected.approvalBandLabel ? `${selected.approvalBandLabel} · ` : ''}
                        Step {approvalStepSummary.done + 1} of {approvalStepSummary.total}:{' '}
                        <strong>{pendingApprovalStep.roleLabel}</strong>
                        {pendingApprovalStep.userName ? ` (${pendingApprovalStep.userName})` : ''}
                      </p>
                    ) : null}
                  </div>
                  <div className="dir-sum">
                    Lines {formatInvoiceMoney(gates.lineSum)}
                    {!gates.ok && gates.issues.some((i) => i.code === 'sum_mismatch') && (
                      <span className="dir-sum-warn"> ≠ total</span>
                    )}
                  </div>
                </div>

                {selected.parseNotes && <p className="dir-notes">{selected.parseNotes}</p>}
                <p className="dir-notes">
                  Job lines are 1099 cash on that job. Overhead is company OpEx only — not a project
                  P&amp;L line. Final approve issues a CRM PO using the selected payment method
                  (Project Card or Bill Payment) for each mapped job. Project Card never creates an
                  ACH payable or QuickBooks bill — pay via the CRM PO. QuickBooks bills are only for
                  under-contract Bill Payment. Training stays Overhead; company OpEx is separate.
                </p>

                <ul className="dir-lines">
                  {draftLines.map((line) => {
                    const isOh = line.phase === 'overhead';
                    const projects = projectsForDeal(line.dealId);
                    return (
                      <li key={line.id} className="dir-line">
                        <div className="dir-line-row">
                          <input
                            className="dir-input dir-input--grow"
                            value={line.description || ''}
                            placeholder="Description"
                            disabled={linesLocked || busy}
                            onChange={(e) => updateLine(line.id, { description: e.target.value })}
                          />
                          <input
                            className="dir-input dir-input--money"
                            type="number"
                            step="0.01"
                            value={line.amount}
                            aria-label="Cash amount"
                            disabled={linesLocked || busy}
                            onChange={(e) =>
                              updateLine(line.id, { amount: Number(e.target.value) || 0 })
                            }
                          />
                          <button
                            type="button"
                            className="dir-icon-btn"
                            aria-label="Remove line"
                            disabled={linesLocked || busy}
                            onClick={() =>
                              setDraftLines((prev) => prev.filter((l) => l.id !== line.id))
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="dir-line-row">
                          <select
                            className="dir-select"
                            value={line.phase}
                            title="When this cash belongs — job phase, or company overhead"
                            disabled={linesLocked || busy}
                            onChange={(e) => {
                              const phase = e.target.value as DesignerInvoicePhase;
                              updateLine(line.id, {
                                phase,
                                ...(phase === 'overhead'
                                  ? { dealId: null, projectId: null, dealLabel: null, projectLabel: null }
                                  : { projectId: null, projectLabel: null }),
                              });
                            }}
                          >
                            {PHASES.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                          <select
                            className="dir-select"
                            value={line.bucket || 'external_design'}
                            disabled={linesLocked || busy}
                            title={
                              BUCKETS.find((b) => b.value === (line.bucket || 'external_design'))
                                ?.hint
                            }
                            onChange={(e) =>
                              updateLine(line.id, {
                                bucket: e.target.value as DesignerInvoiceBucket,
                              })
                            }
                          >
                            {BUCKETS.map((b) => (
                              <option key={b.value} value={b.value} title={b.hint}>
                                {b.label}
                              </option>
                            ))}
                          </select>
                          <input
                            className="dir-input dir-input--sm"
                            type="number"
                            step="0.25"
                            placeholder="Hours"
                            value={line.hours ?? ''}
                            disabled={linesLocked || busy}
                            onChange={(e) =>
                              updateLine(line.id, {
                                hours: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        {!isOh && (
                          <div className="dir-line-row dir-line-row--map">
                            <div className="dir-field dir-field--grow">
                              <span className="dir-field-label">Job</span>
                              <QBDealPicker
                                value={line.dealId || ''}
                                disabled={linesLocked || busy}
                                onChange={(dealId) => {
                                  const deal = dealOptions.find((d) => d.id === dealId);
                                  updateLine(line.id, {
                                    dealId: dealId || null,
                                    dealLabel: deal?.label || null,
                                    projectId: null,
                                    projectLabel: null,
                                  });
                                }}
                                dealOptions={dealOptions}
                                emptyLabel="Select job"
                                unmatched={!line.dealId}
                                aria-label="Job"
                              />
                            </div>
                            {projects.length > 1 && (
                              <div className="dir-field">
                                <span className="dir-field-label">Hub project</span>
                                <select
                                  className="dir-select"
                                  value={line.projectId || ''}
                                  aria-label="Hub project"
                                  disabled={linesLocked || busy}
                                  title="Which Hub record this cash sits on when a job has estimating and under-contract twins"
                                  onChange={(e) => {
                                    const projectId = e.target.value || null;
                                    const project = timberProjects.find((p) => p.id === projectId);
                                    updateLine(line.id, {
                                      projectId,
                                      projectLabel: project ? project.name || project.id : null,
                                    });
                                  }}
                                >
                                  <option value="">Select Hub project</option>
                                  {projects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {hubProjectOptionLabel(p)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                        {isOh && (
                          <p className="dir-oh-note">
                            Overhead — company OpEx tag (no job; never job margin).
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  className="dir-add-line"
                  disabled={linesLocked || busy}
                  onClick={() => setDraftLines((prev) => [...prev, newEmptyLine(prev.length)])}
                >
                  <Plus size={14} /> Add line
                </button>

                {!gates.ok && (
                  <ul className="dir-gates">
                    {gates.issues.map((issue, idx) => (
                      <li key={`${issue.code}-${idx}`}>{issue.message}</li>
                    ))}
                  </ul>
                )}

                {issuePo && (
                  <div className="dir-field dir-payment-method">
                    <span className="dir-field-label">Payment method</span>
                    <select
                      className="dir-select"
                      value={paymentMethodId}
                      aria-label="Payment method"
                      title={DESIGNER_INVOICE_PAYMENT_METHOD_DEFAULT_NOTE}
                      disabled={busy}
                      onChange={(e) =>
                        void handlePaymentMethodChange(e.target.value as DesignerInvoicePaymentMethodId)
                      }
                    >
                      {DESIGNER_INVOICE_PAYMENT_METHOD_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="dir-oh-note">{DESIGNER_INVOICE_PAYMENT_METHOD_DEFAULT_NOTE}</p>
                  </div>
                )}

                {error && <div className="dir-status dir-status--error">{error}</div>}
                {issueNote && <div className="dir-status dir-status--ok">{issueNote}</div>}

                <div className="dir-actions">
                  <button
                    type="button"
                    className="dir-btn dir-btn--reject"
                    disabled={busy}
                    onClick={() => void handleReject()}
                  >
                    <X size={14} /> Reject
                  </button>
                  <button
                    type="button"
                    className="dir-btn dir-btn--approve"
                    disabled={busy || !gates.ok}
                    onClick={() => void handleApprove()}
                    title={
                      !gates.ok
                        ? 'Fix gate issues first'
                        : linesLocked && pendingApprovalStep
                          ? `Record approval for ${pendingApprovalStep.roleLabel}. CRM PO on the final gate (QuickBooks bill only for under-contract Bill Payment).`
                          : issuePo
                            ? `Advance approval. CRM PO (${designerInvoicePaymentMethodLabel(paymentMethodId)}) issues only on the final gate.`
                            : 'Approve into CFO ledger'
                    }
                  >
                    {busy ? <Loader2 size={14} className="dir-spin" /> : <Check size={14} />}
                    {linesLocked && pendingApprovalStep
                      ? approvalStepSummary && approvalStepSummary.done + 1 >= approvalStepSummary.total
                        ? `Approve final gate (${pendingApprovalStep.roleLabel})`
                        : `Approve ${pendingApprovalStep.roleLabel}`
                      : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DesignerInvoiceReviewPanel;
